import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useAuth } from '../../../../authContext';
import { ValidationTaskV3 } from '../../../../services/dataService';
import { Dna01Icon, Medicine02Icon } from '../../../components/icons';
import { Button } from '../../../components/button';
import { Modal, Tabs } from 'antd';
import { useState } from 'react';
import { ArrowRightIcon, Link2Icon } from 'lucide-react';
import { Form } from 'react-router-dom';
import { ExtractedDetails } from '../../dashboard/components/articleView';
import GenericForm from '../genericForm';
import DrugSpecificForm from '../drugSpecificForm';

const ArticleCard = ({ data, onSelect }) => {
  return (
    <div
      className="border border-neutral-200 rounded-lg p-4 flex flex-col justify-between"
      //   onClick={() => onSelect(data)}
    >
      <div>
        <div className="flex gap-4 text-xs">
          {data.validation_status === 'pending' ? (
            <span className="text-orange-500 bg-orange-100 px-2 py-1 rounded-md">Pending</span>
          ) : (
            <span className="text-green-500 bg-green-100 px-2 py-1 rounded-md">Completed</span>
          )}
          <div className="text-neutral-500 bg-neutral-100 px-2 py-1 rounded-md">PMID: {data.pubmed_id}</div>
          <div className="text-neutral-500 bg-neutral-100 px-2 py-1 rounded-md flex items-center gap-2">
            <Medicine02Icon className="w-4 h-4" /> {data.drug}
          </div>
        </div>
        <div className="text-sm text-neutral-600 mt-4 font-semibold">{data.generic_article?.title}</div>
      </div>
      <div className="flex mt-4">
        <Button onClick={() => onSelect(data)}>Validate</Button>
      </div>
    </div>
  );
};

const ConsensusModal = ({ articleId, onClose }) => {
  const consensusArticle = useQuery(['consensus_article', articleId], () =>
    ValidationTaskV3.getConsensusArticleDetails(articleId).then((res) => res),
  );
  //   const [form] = Form.useForm();

  if (consensusArticle.isLoading || !consensusArticle.data) {
    return <div>Loading...</div>;
  }
  return (
    <Modal
      title={
        <div className="flex justify-between">
          <div>Validate Article</div>
          <div className="text-sm text-neutral-500 mr-8">
            <Button
              type="link"
              onClick={() => onClose()}
              className="text-blue-500 bg-blue-50 hover:bg-blue-100 rounded-sm px-2 py-1 flex items-center gap-2"
            >
              Next Task <ArrowRightIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      }
      open={articleId ? true : false}
      onCancel={() => {
        onClose();
      }}
      //   onOk={() => form.submit()}
      width={1400}
      destroyOnClose={true}
      footer={null}
    >
      {consensusArticle.data && (
        <div className="flex gap-6 h-[80vh]">
          <div className="w-1/2 overflow-y-auto">
            <div className="flex flex gap-2">
              <button
                className="text-sm text-blue-500 bg-blue-50 rounded-sm px-2 py-1 flex items-center gap-2"
                onClick={() => {
                  window.open(`https://pubmed.ncbi.nlm.nih.gov/${consensusArticle.data.pubmed_id}`, '_blank');
                }}
              >
                <Link2Icon className="w-4 h-4 text-blue-500" />
                {consensusArticle.data.pubmed_id || 'N/A'}
              </button>
              <div className="text-sm text-blue-500 bg-blue-50 rounded-sm px-2 py-1 flex items-center gap-2">
                <Medicine02Icon className="w-4 h-4 text-blue-500" />
                {consensusArticle.data.drug}
              </div>
              <div className="text-sm text-blue-500 bg-blue-50 rounded-sm px-2 py-1 flex items-center gap-2">
                <Dna01Icon className="w-4 h-4 text-blue-500" />
                {consensusArticle.data.disease || 'N/A'}
              </div>
            </div>
            <div className="text-sm font-semibold mt-4">Title</div>
            <div className="text-sm">{consensusArticle.data.generic_article.title}</div>

            <div className="text-sm font-semibold  mt-4 ">Abstract</div>
            <div className="text-sm leading-[175%] ">{consensusArticle.data.generic_article.abstract}</div>

            <div className="text-sm font-semibold mt-8 text-blue-500">Extracted Details</div>
            <ExtractedDetails article={consensusArticle.data} />
          </div>
          <div className="w-1/2 overflow-y-auto">
            <Tabs>
              <Tabs.TabPane tab="Validations" key="validations">
                <div>
                  <div className="text-lg font-semibold mt-4 mb-2">Drugs</div>
                  {consensusArticle.data.generic_annotations.map((validation) => (
                    <div key={validation.id}>
                      <div className="text-sm font-semibold mt-4 mb-2 rounded-md bg-blue-100 text-blue-400 inline-block px-2 py-1 ">
                        Validated by:{validation.validator}
                      </div>
                      <div className="text-sm font-semibold mt-4 mb-2">
                        <div className="flex w-[700px]">
                          <div className="basis-1/3">
                            <div className="text-sm font-semibold text-neutral-500 mb-2">Drugs Studied</div>
                          </div>
                          <div className="flex flex-col gap-1 basis-2/3 ">
                            {validation.drugs &&
                              validation.drugs.map((drug, idx) => (
                                <div key={idx} className="text-sm text-neutral-500">
                                  {drug.name}
                                  <span
                                    className={`text-xs ml-2 px-2 py-0.5 rounded-md ${
                                      drug.is_studied ? 'bg-green-100 text-green-500' : 'bg-yellow-100 text-yellow-500'
                                    }`}
                                  >
                                    {drug.is_studied ? 'Studied' : 'Not Studied'}
                                  </span>
                                  <span
                                    className={`text-xs ml-2 px-2 py-0.5 rounded-md ${
                                      drug.is_randomized
                                        ? 'bg-green-100 text-green-500'
                                        : 'bg-yellow-100 text-yellow-500'
                                    }`}
                                  >
                                    {drug.is_randomized ? 'Randomized' : 'Not Randomized'}
                                  </span>
                                </div>
                              ))}
                            {validation.drugs && validation.drugs.length === 0 && (
                              <div className="text-sm text-neutral-500">No drugs studied</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="text-lg font-semibold mt-4 mb-2">Diseases</div>
                  {consensusArticle.data.generic_annotations.map((validation) => (
                    <div key={validation.id}>
                      <div className="text-sm font-semibold mt-4 mb-2 rounded-md bg-blue-100 text-blue-400 inline-block px-2 py-1 ">
                        Validated By: {validation.validator}
                      </div>
                      <div className="text-sm font-semibold mt-4 mb-2">
                        {
                          <div className="flex w-[700px]">
                            <div className="basis-1/3">
                              <div className="text-sm font-semibold text-neutral-500 mb-2">Disease Types</div>
                            </div>
                            <div className="flex flex-col gap-1 basis-2/3">
                              {validation.diseases &&
                                validation.diseases.map((disease, idx) => (
                                  <div key={idx} className="text-sm text-neutral-500">
                                    {disease.name}
                                    <span
                                      className={`text-xs ml-2 px-2 py-0.5 rounded-md ${
                                        disease.is_studied
                                          ? 'bg-green-100 text-green-500'
                                          : 'bg-yellow-100 text-yellow-500'
                                      }`}
                                    >
                                      {disease.is_studied ? 'Studied' : 'Not Studied'}
                                    </span>
                                  </div>
                                ))}
                              {validation.diseases && validation.diseases.length === 0 && (
                                <div className="text-sm text-neutral-500">No disease types</div>
                              )}
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                  ))}

                  <div className="text-lg font-semibold mt-4 mb-2">Endpoints</div>
                  {consensusArticle.data.generic_annotations.map((validation) => (
                    <div key={validation.id}>
                      <div className="text-sm font-semibold mt-4 mb-2 rounded-md bg-blue-100 text-blue-400 inline-block px-2 py-1 ">
                        Validated By: {validation.validator}
                      </div>
                      <div className="text-sm font-semibold mt-4 mb-2">
                        {' '}
                        <div className="flex w-[700px]">
                          <div className="basis-1/3">
                            <div className="text-sm font-semibold text-neutral-500 mb-2">Endpoints</div>
                          </div>
                          <div className="flex flex-col gap-1 basis-2/3">
                            {validation.endpoints &&
                              validation.endpoints.map((endpoint, idx) => (
                                <div key={idx} className="text-sm text-neutral-500">
                                  {endpoint.name}
                                  <span
                                    className={`text-xs ml-2 px-2 py-0.5 rounded-md bg-neutral-100 text-neutral-500`}
                                  >
                                    {endpoint.category}
                                  </span>
                                </div>
                              ))}
                            {validation.endpoints && validation.endpoints.length === 0 && (
                              <div className="text-sm text-neutral-500">No endpoints</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <div className="text-lg font-semibold mt-4 mb-2">Number of Patients</div>
                  {consensusArticle.data.generic_annotations.map((validation) => (
                    <div key={validation.id} className="flex gap-16 items-center">
                      <div className="text-sm font-semibold mt-4 mb-2 rounded-md bg-blue-100 text-blue-400 inline-block px-2 py-1 ">
                        Validated By: {validation.validator}
                      </div>
                      <div className="text-sm font-semibold mt-4 mb-2">{validation.num_patients || '-'}</div>
                    </div>
                  ))}

                  <div className="text-lg font-semibold mt-4 mb-2">Study Type</div>
                  {consensusArticle.data.generic_annotations.map((validation) => (
                    <div key={validation.id} className="flex gap-16 items-center">
                      <div className="text-sm font-semibold mt-4 mb-2 rounded-md bg-blue-100 text-blue-400 inline-block px-2 py-1 ">
                        Validated By: {validation.validator}
                      </div>
                      <div className="text-sm font-semibold mt-4 mb-2">{validation.study_type}</div>
                    </div>
                  ))}
                </div>
              </Tabs.TabPane>
              <Tabs.TabPane tab="Consensus" key="consensus">
                <div>
                  <GenericForm
                    // initialValues={genericInitialData}
                    validationTaskId={articleId}
                    articleId={articleId}
                    validator={'vivek'}
                    isValidated={false}
                  />
                  <DrugSpecificForm
                    // initialValues={drugSpecificInitialData}
                    validationTaskId={articleId}
                    articleId={articleId}
                    validator={'vivek'}
                    isValidated={false}
                  />
                </div>
              </Tabs.TabPane>
            </Tabs>
          </div>
        </div>
      )}
    </Modal>
  );
};

export const ArticlesForConsensus = ({ id }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedArticle, setSelectedArticle] = useState(null);
  const consensusArticles = useQuery(
    ['consensus_articles'],
    () => ValidationTaskV3.getConsensusArticles().then((res) => res),
    {
      cacheTime: 0,
    },
  );

  const submitMutation = useMutation(
    (data) => {
      return ValidationTaskV3.saveAnnotation(id, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['consensus_articles']);
        consensusArticles.refetch();
      },
    },
  );
  return (
    <div>
      {consensusArticles.data?.map((article) => (
        <ArticleCard key={article.id} data={article} onSelect={setSelectedArticle} />
      ))}
      {selectedArticle && (
        <ConsensusModal
          articleId={selectedArticle ? selectedArticle.id : null}
          onClose={() => setSelectedArticle(null)}
        />
      )}
    </div>
  );
};
